import { Account } from '@/client/accounts';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { branchAdminCheck } from '@/utils/helpers';
import classNames from 'classnames';
import { MenuItem } from 'primereact/menuitem';
import { TabMenu } from 'primereact/tabmenu';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

type AccountTabsProps = {
  account: Account;
  systemSettingsEnabled?: boolean;
  onNavigate: (url: string) => void;
};

const getMenuItems = (
  account: Account,
  currentAccount: Account,
  can: (action: Actions, subject: Subjects) => boolean,
  t: TranslationFunctionType,
  isBranchAdmin: boolean,
  showSystemSettings: boolean,
  onNavigate: (url: string) => void,
): MenuItem[] => {
  const items: MenuItem[] = [
    {
      label: t('account.general'),
      template: getItemTemplate(
        !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        location.pathname,
        onNavigate,
      ),
      data: !currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    },
  ];

  items.push({
    label: t('account.emailsAndDelivery'),
    template: getItemTemplate(
      currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_EMAILS_AND_DELIVERY](
            account?.id,
          )
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_EMAILS_AND_DELIVERY](),
      location.pathname,
      onNavigate,
    ),
    data: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_EMAILS_AND_DELIVERY](
          account?.id,
        )
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_EMAILS_AND_DELIVERY](),
  });

  items.push({
    label: t('account.trainingSettings'),
    template: getItemTemplate(
      currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_TRAINING_SETTINGS](
            account?.id,
          )
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_TRAINING_SETTINGS](),
      location.pathname,
      onNavigate,
    ),
    data: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_TRAINING_SETTINGS](account?.id)
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_TRAINING_SETTINGS](),
  });

  if (
    can(Actions.READ, Subjects.ACCOUNT_SSO) &&
    can(Actions.READ, Subjects.AD_SYNC) &&
    !isBranchAdmin
  ) {
    items.push({
      label: t('integrations'),
      template: getItemTemplate(
        !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS]()
          : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS](account?.id),
        location.pathname,
        onNavigate,
      ),
      data: {
        urls: [
          !currentAccount?.isSystem
            ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS]()
            : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS](
                account?.id,
              ),
          !currentAccount?.isSystem
            ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_MICROSOFT]()
            : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS_MICROSOFT](
                account?.id,
              ),
          !currentAccount?.isSystem
            ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_GOOGLE]()
            : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS_GOOGLE](
                account?.id,
              ),
        ],
      },
    });
  } else {
    if (can(Actions.READ, Subjects.AD_SYNC) && !isBranchAdmin) {
      items.push({
        label: t('account.microsoft'),
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.ACCOUNT_AZURE_AD](account?.id),
          location.pathname,
          onNavigate,
        ),
        data: RedirectPaths[RedirectPathsEnum.ACCOUNT_AZURE_AD](account?.id),
      });
    }

    if (can(Actions.READ, Subjects.ACCOUNT_SSO) && !isBranchAdmin) {
      items.push({
        label: t('sso'),
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.ACCOUNT_SSO](account?.id),
          location.pathname,
          onNavigate,
        ),
        data: RedirectPaths[RedirectPathsEnum.ACCOUNT_SSO](account?.id),
      });
    }
  }

  items.push({
    label: t('account.dataHandling'),
    template: getItemTemplate(
      currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_DATA_CONTROL](account?.id)
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_DATA_CONTROL](),
      location.pathname,
      onNavigate,
    ),
    data: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_DATA_CONTROL](account?.id)
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_DATA_CONTROL](),
  });

  if (
    currentAccount &&
    (currentAccount?.isSystem || showSystemSettings) &&
    can(Actions.READ, Subjects.ACCOUNT_INTERNAL_TAB)
  ) {
    items.push({
      label: t('account.internal'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTERNAL](account?.id),
        location.pathname,
        onNavigate,
      ),
      data: {
        urls: [
          RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTERNAL](account?.id),
          RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTERNAL_GO_PHISH](
            account?.id,
          ),
          RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTERNAL_HUBSPOT](
            account?.id,
          ),
        ],
      },
    });
  }

  if (
    (currentAccount?.isSystem || showSystemSettings) &&
    can(Actions.READ, Subjects.ACCOUNT_CONTENT_TAB)
  ) {
    items.push(
      {
        label: t('content'),
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSES](account?.id),
          location.pathname,
          onNavigate,
        ),
        data: RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSES](account?.id),
      },
      {
        label: t('materials'),
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.ACCOUNT_SECURITY_DOCUMENTS](
            account?.id,
          ),
          location.pathname,
          onNavigate,
        ),
        data: RedirectPaths[RedirectPathsEnum.ACCOUNT_SECURITY_DOCUMENTS](
          account.id,
        ),
      },
    );
  }

  if (
    (currentAccount?.isSystem || showSystemSettings) &&
    can(Actions.READ, Subjects.ACCOUNT_FEATURES_TAB)
  ) {
    items.push({
      label: t('features'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.ACCOUNT_FEATURE_FLAGS](account?.id),
        location.pathname,
        onNavigate,
      ),
      data: RedirectPaths[RedirectPathsEnum.ACCOUNT_FEATURE_FLAGS](account?.id),
    });
  }

  if (
    can(Actions.READ, Subjects.ACCOUNTS) ||
    can(Actions.READ, Subjects.OWN_ACCOUNT)
  ) {
    items.push({
      label: t('securityProfile.title'),
      template: getItemTemplate(
        currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_SECURITY_PROFILE](
              account?.id,
            )
          : RedirectPaths[RedirectPathsEnum.ACCOUNT_SECURITY_PROFILE](),
        location.pathname,
        onNavigate,
      ),
      data: currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_SECURITY_PROFILE](
            account?.id,
          )
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_SECURITY_PROFILE](),
    });
  }
  if (!isBranchAdmin) {
    items.push({
      label: t('api'),
      template: getItemTemplate(
        currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_API](account?.id)
          : RedirectPaths[RedirectPathsEnum.ACCOUNT_API](),
        location.pathname,
        onNavigate,
      ),
      data: currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_API](account?.id)
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_API](),
    });
  }

  return items;
};

const getItemTemplate =
  (url: string, current: string, onNavigate: (url: string) => void) =>
  (item: MenuItem) =>
    (
      <a
        className={classNames({
          'p-menuitem-link': true,
          'p-menuitem-link-active': current.includes(url),
        })}
        role="menuitem"
        onClick={() => onNavigate(url)}
        style={{ cursor: 'pointer' }}
      >
        <span className="p-menuitem-text">{item.label}</span>
      </a>
    );

export const AccountTabs: React.FC<AccountTabsProps> = ({
  account,
  systemSettingsEnabled,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { can } = usePermission();
  const isBranchAdmin = branchAdminCheck(currentUser, currentAccount);

  const items = getMenuItems(
    account as Account,
    currentAccount as Account,
    can,
    t,
    isBranchAdmin,
    systemSettingsEnabled ?? false,
    onNavigate,
  );

  const currentPath = location.pathname;
  const [activeIndex, setActiveIndex] = useState<number>(
    items.findIndex(({ data }: MenuItem) =>
      data?.urls
        ? data.urls.some((path: string) => path === currentPath)
        : data === currentPath,
    ),
  );

  return (
    <TabMenu
      model={items}
      activeIndex={activeIndex}
      onTabChange={(e) => setActiveIndex(e.index)}
    />
  );
};
